import {Component, computed, inject, input, output, InputSignal, SimpleChanges, OutputEmitterRef, type WritableSignal, signal} from '@angular/core';
import {Order, PageControl, TableColumn} from '../../../interfaces/application';
import {TableModule} from 'primeng/table';
import {CommonModule} from '@angular/common';
import {Statement} from '../../../interfaces/statement';
import {StatementService} from '../../../services/statement.service';
import dayjs from 'dayjs';
import {environment} from '../../../../environment/environment';
import {finalize, lastValueFrom} from 'rxjs';
import {Tooltip} from 'primeng/tooltip';
import {ConfirmPopup} from 'primeng/confirmpopup';
import { PaginatorModule, type PaginatorState } from 'primeng/paginator';
import {ConfirmationService} from 'primeng/api';
import { LottieComponent, type AnimationOptions } from 'ngx-lottie';

@Component({
  selector: 'app-table-statement-account',
  imports: [
    CommonModule,
    TableModule,
    // Tooltip,
    // ConfirmPopup,
    PaginatorModule,
    LottieComponent
  ],
  providers: [
    ConfirmationService
  ],
  templateUrl: './table-statement-account.html',
  styleUrl: './table-statement-account.css'
})
export class TableStatementAccount {

  public emptyLottieOptions: AnimationOptions = {
    path: 'assets/lottie/empty.json',
    loop: true,
    autoplay: true,
  };

  private readonly _confirmationService: ConfirmationService = inject(ConfirmationService);
  private readonly _statementService: StatementService = inject(StatementService);

  public localLoading: WritableSignal<boolean> = signal<boolean>(false);

  public loading: InputSignal<boolean> = input<boolean>(false);
  public cashier_account_id: InputSignal<string> = input<string>('');
  public search_term: InputSignal<string> = input<string>('');
  public start_date: InputSignal<string> = input<string>('');
  public end_date: InputSignal<string> = input<string>('');

  public export: OutputEmitterRef<string> = output<string>();
  public edit: OutputEmitterRef<Statement> = output<Statement>();
  public delete: OutputEmitterRef<string> = output<string>();

  public items: Statement[] = [];

  public columns: TableColumn[] = [
    {
      label: 'Data/Hora',
      slug: 'launch_at',
      align: 'text-start',
      sortable: true,
    },
    {
      label: 'Id',
      slug: 'id',
      align: 'text-start',
      sortable: true,
    },
    {
      label: 'Conta',
      slug: 'account',
      align: 'text-start',
      sortable: false,
    },
    {
      label: 'Descrição',
      slug: 'description',
      align: 'text-start',
      sortable: true,
    },
    {
      label: 'Valor',
      slug: 'value',
      align: 'text-start',
      sortable: true,
    },
    {
      label: 'Saldo',
      slug: 'balance',
      align: 'text-start',
      sortable: false,
    },
    // {
    //   label: 'Ações',
    //   slug: '',
    //   align: 'text-start',
    //   sortable: false,
    // },
  ];

  public pageControl: PageControl = {
    page: 0,
    take: 10,
    itemCount: 0,
    pageCount: 0,
    orderField: 'launch_at',
    order: Order.DESC
  }

  protected readonly order: typeof Order = Order;

  public ngOnChanges(changes: SimpleChanges): void {
    const {
      loading,
      cashier_account_id,
      search_term,
      start_date,
      end_date,
    } = changes;

    if (!loading?.currentValue) {
      this.loadData();
    }
    else if (cashier_account_id?.currentValue && cashier_account_id?.currentValue !== cashier_account_id?.previousValue) {
      this.loadData();
    }
    else if (search_term?.currentValue && search_term?.currentValue !== search_term?.previousValue) {
      this.loadData();
    }
    else if (start_date?.currentValue && start_date?.currentValue !== start_date?.previousValue) {
      this.loadData();
    }
    else if (end_date?.currentValue && end_date?.currentValue !== end_date?.previousValue) {
      this.loadData();
    }
  }

  public loadData(): void {

    const filters = {
      search_term: this.search_term() || '',
      cashier_account_id: this.cashier_account_id() || '',
      start_date: this.start_date() ? dayjs(this.start_date()).format('YYYY-MM-DD') : '',
      end_date: this.end_date() ? dayjs(this.end_date()).format('YYYY-MM-DD') : '',
    };

    this.localLoading.set(true);
    this._statementService.get(this.pageControl, filters)
      .pipe(finalize(() => this.localLoading.set(false)))
      .subscribe({
        next: response => {
          this.items = response.data;

          this.pageControl.page = response.meta.current_page;
          this.pageControl.itemCount = response.meta.total;
        }
      })
  }

  public sumValue(): number {
    return this.items.reduce((prev, curr) => prev + curr.value, 0);
  }

  public sumBalance(): number {
    return this.items.reduce((prev, curr) => prev + curr.balance, 0);
  }

  public sort(slug: string): void {
    this.pageControl.page = 0;
    this.pageControl.orderField = slug;
    this.pageControl.order = this.pageControl.order === Order.ASC ? Order.DESC : Order.ASC;

    this.loadData();
  }

  public confirmDelete(id: string, event: Event): void {
    this._confirmationService.confirm({
      target: event.target as EventTarget,
      message: 'Deseja remover este lançamento?',
      acceptLabel: 'Remover',
      rejectLabel: 'Cancelar',
      rejectButtonProps: {
        outlined: true,
      },
      acceptButtonProps: {
        severity: 'danger',
      },
      accept: () => {
        this.delete.emit(id)
      },
    });
  }

  public onPageChange(e: PaginatorState): void {
    this.pageControl.page = (e?.page || 0) + 1;
    this.pageControl.take = (e?.rows || 10);

    this.loadData();
  }
}
